/**
 * Dialler initialization utilities
 * Separated to avoid circular dependencies between diallerState and stores
 */

import { addWebsocketListener, showToast } from "@sourcewhale/core";
import { attendeesState } from "../attendeesState.svelte";
import { diallerState } from "../diallerState.svelte";

let deviceMonitoringInitialized = false;
let deviceChangeHandler = null;
let deviceChangeTimeout = null;
let permissionResult = null;
let permissionChangeHandler = null;
let websocketActive = false;

const initializeDeviceMonitoring = () => {
  if (deviceMonitoringInitialized) {
    return;
  }

  const isMediaDevicesSupported =
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.enumerateDevices === "function";

  if (!isMediaDevicesSupported) {
    return;
  }

  const enumerateDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === "audioinput");
      const outputs = devices.filter((d) => d.kind === "audiooutput");

      diallerState.updateAvailableDevices({ inputs, outputs });
    } catch {
      // Error enumerating devices
    }
  };

  deviceChangeHandler = () => {
    if (deviceChangeTimeout) {
      clearTimeout(deviceChangeTimeout);
    }
    deviceChangeTimeout = setTimeout(enumerateDevices, 300);
  };

  navigator.mediaDevices.addEventListener("devicechange", deviceChangeHandler);
  enumerateDevices();

  if (navigator.permissions && navigator.permissions.query) {
    navigator.permissions
      .query({ name: "microphone" })
      .then((result) => {
        permissionResult = result;
        diallerState.microphonePermission = result.state;

        permissionChangeHandler = () => {
          diallerState.microphonePermission = result.state;
        };

        result.addEventListener("change", permissionChangeHandler);
      })
      .catch(() => {
        // Cannot query microphone permission state
      });
  }

  deviceMonitoringInitialized = true;
};

const initializeWebSocketListener = () => {
  if (websocketActive) {
    return;
  }

  addWebsocketListener((message) => {
    if (message.kind === "incompleteDiallerCall") {
      showToast({ title: message.warning, type: "warning" });
    }

    if (message.kind === "diallerConferenceAttendeesChanged") {
      attendeesState.convertCallToAttendees(message);
    }

    if (message.kind === "diallerConferenceInviteChanged") {
      if (["accepted", "declined"].includes(message.invite.status)) {
        diallerState.inviteResponse(message);
      }
    }
  });

  websocketActive = true;
};

/**
 * Load initial data when dialler opens
 */
export const loadDiallerData = () => {
  initializeDeviceMonitoring();
  initializeWebSocketListener();
};

/**
 * Clean up initialization state
 */
export const cleanupInitialization = () => {
  if (deviceChangeTimeout) {
    clearTimeout(deviceChangeTimeout);
    deviceChangeTimeout = null;
  }

  if (deviceChangeHandler && navigator.mediaDevices) {
    navigator.mediaDevices.removeEventListener(
      "devicechange",
      deviceChangeHandler,
    );
    deviceChangeHandler = null;
  }

  if (permissionResult && permissionChangeHandler) {
    permissionResult.removeEventListener("change", permissionChangeHandler);
    permissionResult = null;
    permissionChangeHandler = null;
  }

  if (websocketActive) {
    websocketActive = false;
  }

  deviceMonitoringInitialized = false;
};
